import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Check, CheckCheck, Trash2 } from 'lucide-react';
import {
    getNotifications,
    getUnreadNotificationCount,
    markNotificationRead,
    markAllNotificationsRead,
    dismissNotification,
    Notification,
} from '../../services/api';
import './Notifications.css';

// ---------------------------------------------------------------------------
// Time formatting helper
// ---------------------------------------------------------------------------
function timeAgo(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString();
}

// ---------------------------------------------------------------------------
// NotificationCentre
// ---------------------------------------------------------------------------

const NotificationCentre: React.FC = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const bellRef = useRef<HTMLButtonElement>(null);

    // ── Fetch unread count (polling, 60s interval) ────────────────────────
    const refreshCount = useCallback(async () => {
        try {
            const data = await getUnreadNotificationCount();
            setUnreadCount(data.unread_count);
        } catch {
            // silently ignore — user may not be authenticated yet
        }
    }, []);

    useEffect(() => {
        refreshCount();
        const interval = setInterval(refreshCount, 60000);
        return () => clearInterval(interval);
    }, [refreshCount]);

    // ── Load notifications when panel opens ───────────────────────────────
    const loadNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getNotifications({ limit: 20 });
            setNotifications(data);
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            loadNotifications();
        }
    }, [open, loadNotifications]);

    // ── Close panel when clicking outside ────────────────────────────────
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                open &&
                panelRef.current &&
                !panelRef.current.contains(e.target as Node) &&
                bellRef.current &&
                !bellRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleBellClick = () => setOpen(o => !o);

    const handleMarkRead = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await markNotificationRead(id);
            setNotifications(prev =>
                prev.map(n => (n.notification_id === id ? { ...n, read: true } : n))
            );
            setUnreadCount(c => Math.max(0, c - 1));
        } catch {
            // ignore
        }
    };

    const handleDismiss = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await dismissNotification(id);
            setNotifications(prev => prev.filter(n => n.notification_id !== id));
            // Recalculate unread count
            const dismissed = notifications.find(n => n.notification_id === id);
            if (dismissed && !dismissed.read) {
                setUnreadCount(c => Math.max(0, c - 1));
            }
        } catch {
            // ignore
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await markAllNotificationsRead();
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch {
            // ignore
        }
    };

    const handleItemClick = async (notif: Notification) => {
        // Mark as read
        if (!notif.read) {
            try {
                await markNotificationRead(notif.notification_id);
                setNotifications(prev =>
                    prev.map(n =>
                        n.notification_id === notif.notification_id ? { ...n, read: true } : n
                    )
                );
                setUnreadCount(c => Math.max(0, c - 1));
            } catch {
                // ignore
            }
        }
        // Navigate if action_url present
        if (notif.action_url) {
            setOpen(false);
            navigate(notif.action_url);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <>
            {/* Bell button */}
            <button
                ref={bellRef}
                className={`notification-bell-btn${unreadCount > 0 ? ' has-unread' : ''}`}
                onClick={handleBellClick}
                title="Notifications"
                aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
            >
                <Bell size={18} />
                {unreadCount > 0 && (
                    <span className="notification-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown panel */}
            {open && (
                <div className="notification-panel" ref={panelRef}>
                    {/* Header */}
                    <div className="notification-panel-header">
                        <h3 className="notification-panel-title">Notifications</h3>
                        <div className="notification-panel-actions">
                            {unreadCount > 0 && (
                                <button
                                    className="notification-mark-all-btn"
                                    onClick={handleMarkAllRead}
                                    title="Mark all as read"
                                >
                                    Mark all read
                                </button>
                            )}
                            <button
                                className="notification-panel-close-btn"
                                onClick={() => setOpen(false)}
                                title="Close"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    {/* List */}
                    <div className="notification-list">
                        {loading && notifications.length === 0 ? (
                            <div className="notification-empty">
                                <span className="notification-empty-text">Loading...</span>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="notification-empty">
                                <span className="notification-empty-icon">
                                    <CheckCheck size={32} />
                                </span>
                                <span className="notification-empty-text">
                                    No notifications — you're all caught up
                                </span>
                                <span className="notification-empty-sub">
                                    AiMe will alert you when something needs attention
                                </span>
                            </div>
                        ) : (
                            notifications.map(notif => (
                                <div
                                    key={notif.notification_id}
                                    className={`notification-item${!notif.read ? ' unread' : ''}${notif.type === 'approval_needed' ? ' approval-needed' : ''}`}
                                    onClick={() => handleItemClick(notif)}
                                >
                                    <span
                                        className={`notification-priority-dot ${notif.type === 'approval_needed' ? 'high' : notif.priority}`}
                                    />
                                    <div className="notification-content">
                                        <p className="notification-item-title">{notif.title}</p>
                                        <p className="notification-item-body">{notif.body}</p>
                                        <div className="notification-item-meta">
                                            <span className="notification-item-time">
                                                {timeAgo(notif.created_at)}
                                            </span>
                                            {notif.action_url && notif.action_label && (
                                                <span className="notification-item-action-btn">
                                                    {notif.action_label}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="notification-item-controls">
                                        {!notif.read && (
                                            <button
                                                className="notification-control-btn"
                                                onClick={e => handleMarkRead(notif.notification_id, e)}
                                                title="Mark as read"
                                            >
                                                <Check size={14} />
                                            </button>
                                        )}
                                        <button
                                            className="notification-control-btn danger"
                                            onClick={e => handleDismiss(notif.notification_id, e)}
                                            title="Dismiss"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="notification-panel-footer">
                            <a
                                href="/coordinator"
                                className="notification-view-all-link"
                                onClick={e => {
                                    e.preventDefault();
                                    setOpen(false);
                                    navigate('/coordinator');
                                }}
                            >
                                View all notifications
                            </a>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default NotificationCentre;
